import React from 'react';
import get from 'lodash/get';
import cnames from 'classnames';
import ToastContainer from '../Toast/async';
import DesignSystemContext from '../contexts/DesignSystemContext';
import TrackingContext from '../contexts/TrackingContext';
import defaultTheme from '../themes/default';

const defaultThemeCss = `
:root {
  --ds-color-brand: #E41421;
  --ds-color-primary: #FF596F;
  --ds-color-neutral-50: #F9F9FB;
  --ds-color-neutral-100: #F2F2F6;
  --ds-color-neutral-200: #D6DADF;
  --ds-color-neutral-300: #9695A8;
  --ds-color-neutral-500: #757195;
  --ds-color-neutral-800: #19124F;
  --ds-color-neutral-900: #07002f;
  --ds-color-success-100: #E7F8EF;
  --ds-color-success-200: #C1F2DA;
  --ds-color-success-500: #34C665;
  --ds-color-success-600: #10834C;
  --ds-color-success-700: #0B532F;
  --ds-color-information-100: #ECE9FF;
  --ds-color-information-200: #CDC3FF;
  --ds-color-information-300: #AFA3F2;
  --ds-color-information-500: #755EED;
  --ds-color-information-600: #5137D7;
  --ds-color-information-700: #36139A;
  --ds-color-warning-100: #FFE9D8;
  --ds-color-warning-200: #FFD6AD;
  --ds-color-warning-500: #FFBB77;
  --ds-color-warning-600: #AD5D02;
  --ds-color-warning-700: #723D0A;
  --ds-color-error-100: #FFECEC;
  --ds-color-error-200: #FFCCCB;
  --ds-color-error-500: #ED3432;
  --ds-color-error-600: #CD1C1A;
  --ds-color-error-700: #810D0C;
  --ds-color-destructive-100: #FFECEC;
  --ds-color-destructive-200: #FFCCCB;
  --ds-color-destructive-500: #ED3432;
  --ds-color-destructive-600: #CD1C1A;
  --ds-color-destructive-700: #810D0C;
  --ds-color-white: #FFFFFF;
  --ds-color-black: #000000;
  --ds-color-transparent: transparent;
  --ds-color-expressive-green-100: #E3F8E9;
  --ds-color-expressive-green-300: #51BA88;
  --ds-color-expressive-green-500: #0E844C;
  --ds-color-expressive-green-700: #0B6339;
  --ds-color-expressive-blue-100: #F0F6FF;
  --ds-color-expressive-blue-300: #ADCEFF;
  --ds-color-expressive-blue-500: #0066FF;
  --ds-color-expressive-blue-700: #0652C3;
  --ds-color-expressive-yellow-100: #FAF2D9;
  --ds-color-expressive-yellow-300: #FFE9A1;
  --ds-color-expressive-yellow-500: #FFD54D;
  --ds-color-expressive-yellow-700: #D8A709;
  --ds-color-expressive-red-100: #FFD6DD;
  --ds-color-expressive-red-300: #F77990;
  --ds-color-expressive-red-500: #D92D02;
  --ds-color-expressive-red-700: #9A0D27;
  --ds-color-expressive-pink-100: #FFECF4;
  --ds-color-expressive-pink-300: #FFADCE;
  --ds-color-expressive-pink-500: #D32168;
  --ds-color-expressive-pink-700: #C30652;
  --ds-color-expressive-teal-100: #E3F7F8;
  --ds-color-expressive-teal-300: #5BCBD1;
  --ds-color-expressive-teal-500: #19AEAE;
  --ds-color-expressive-teal-700: #086166;
  --ds-color-expressive-purple-100: #FDDDFF;
  --ds-color-expressive-purple-300: #F1A7F4;
  --ds-color-expressive-purple-500: #C511CE;
  --ds-color-expressive-purple-700: #86338B;
  --ds-color-expressive-orange-100: #FFEBD8;
  --ds-color-expressive-orange-300: #FFAF75;
  --ds-color-expressive-orange-500: #EA872D;
  --ds-color-expressive-orange-700: #B06215;
  --ds-color-border: var(--ds-color-neutral-200);
  --ds-color-border-light: var(--ds-color-neutral-100);
  --ds-color-text: var(--ds-color-neutral-800);
  --ds-color-text-contrast: #ffffff;
  --ds-color-text-muted: var(--ds-color-neutral-500);
  --ds-color-text-muted-contrast: var(--ds-color-neutral-300);
  --ds-color-background-ui: var(--ds-color-white);
  --ds-color-background-ui-contrast: var(--ds-color-neutral-900);
  --ds-color-background-ui-medium: var(--ds-color-white);
  --ds-color-background-ui-high: var(--ds-color-white);
  --ds-color-background-alt-50: rgba(245, 245, 248, 0.60);
  --ds-color-background-alt-100: rgba(233, 233, 240, 0.60);
  --ds-color-background-alt-200: rgba(187, 193, 202, 0.60);
  --ds-color-background-alt-500: rgba(63, 78, 105, 0.60);
  --ds-color-background-app: var(--ds-color-white);
  --ds-color-background-app-light: var(--ds-color-white);
  --ds-color-background-app-dark: var(--ds-color-neutral-900);
  --ds-color-background-overlay: rgba(140, 149, 165, 0.6);
  --ds-color-background-overlay-transparent: rgba(140, 149, 165, 0);
  --ds-color-state-disabled-text: var(--ds-color-neutral-300);
  --ds-color-state-disabled-background: var(--ds-color-background-alt-100);
  --ds-color-state-focus-border: var(--ds-color-information-300);
  --ds-color-state-hover-overlay: rgba(26, 18, 57, 0.05);
  --ds-color-state-hover-overlay-contrast: rgba(255, 255, 255, 0.1);
  --ds-color-state-loading-background: var(--ds-color-background-alt-200);
  --ds-color-state-loading-shimmer: var(--ds-color-background-alt-50);
  --ds-font-family: -apple-system, BlinkMacSystemFont, Inter, Arial, sans-serif;
  --ds-font-weight-light: 400;
  --ds-font-weight-normal: 400;
  --ds-font-weight-bold: 600;
  --ds-font-weight-display1: var(--ds-font-weight-bold);
  --ds-font-weight-display2: var(--ds-font-weight-bold);
  --ds-font-weight-display3: var(--ds-font-weight-bold);
  --ds-font-weight-display4: var(--ds-font-weight-normal);
  --ds-font-weight-display5: var(--ds-font-weight-bold);
  --ds-font-weight-display6: var(--ds-font-weight-bold);
  --ds-font-weight-body: var(--ds-font-weight-normal);
  --ds-font-weight-body2: var(--ds-font-weight-normal);
  --ds-font-weight-caption: var(--ds-font-weight-normal);
  --ds-font-size-display1: 36px;
  --ds-font-size-display2: 24px;
  --ds-font-size-display3: 20px;
  --ds-font-size-display4: 20px;
  --ds-font-size-display5: 16px;
  --ds-font-size-display6: 14px;
  --ds-font-size-body: 16px;
  --ds-font-size-body2: 14px;
  --ds-font-size-caption: 12px;
  --ds-line-height-display1: 48px;
  --ds-line-height-display2: 32px;
  --ds-line-height-display3: 30px;
  --ds-line-height-display4: 30px;
  --ds-line-height-display5: 26px;
  --ds-line-height-display6: 24px;
  --ds-line-height-body: 26px;
  --ds-line-height-body2: 24px;
  --ds-line-height-caption: 16px;
  --ds-radius-small: 4px;
  --ds-radius-medium: 8px;
  --ds-radius-large: 12px;
  --ds-radius-pill: 200vh;
  --ds-size-border-width-hairline: 1px;
  --ds-size-border-width-small: 2px;
  --ds-size-border-width-medium: 4px;
  --ds-spacing-xxxs: 2px;
  --ds-spacing-xxs: 4px;
  --ds-spacing-xs: 8px;
  --ds-spacing-s: 12px;
  --ds-spacing-m: 16px;
  --ds-spacing-l: 24px;
  --ds-spacing-xl: 32px;
  --ds-spacing-xxl: 48px;
  --ds-spacing-xxxl: 64px;
  --ds-spacing-legacy-unit: 16px;
  --ds-spacing: var(--ds-spacing-xxxs);
  --ds-spacing-01: var(--ds-spacing-xxxs);
  --ds-spacing-02: var(--ds-spacing-xxs);
  --ds-spacing-03: var(--ds-spacing-xs);
  --ds-spacing-04: var(--ds-spacing-s);
  --ds-spacing-05: var(--ds-spacing-m);
  --ds-spacing-06: var(--ds-spacing-m);
  --ds-spacing-07: var(--ds-spacing-l);
  --ds-spacing-08: var(--ds-spacing-l);
  --ds-spacing-09: var(--ds-spacing-xl);
  --ds-spacing-10: var(--ds-spacing-xxl);
  --ds-size-dimension: var(--ds-spacing-xxs);
  --ds-size-dimension-xxxs: var(--ds-size-dimension);
  --ds-size-dimension-xxs: calc(var(--ds-size-dimension) * 2);
  --ds-size-dimension-xs: calc(var(--ds-size-dimension) * 3);
  --ds-size-dimension-s: calc(var(--ds-size-dimension) * 4);
  --ds-size-dimension-m: calc(var(--ds-size-dimension) * 6);
  --ds-size-dimension-l: calc(var(--ds-size-dimension) * 8);
  --ds-size-dimension-xl: calc(var(--ds-size-dimension) * 10);
  --ds-size-dimension-xxl: calc(var(--ds-size-dimension) * 12);
  --ds-size-dimension-xxxl: calc(var(--ds-size-dimension) * 16);
  --ds-size-dimension-xxxxl: calc(var(--ds-size-dimension) * 24);
  --ds-size-dimension-01: var(--ds-size-dimension-xxxs);
  --ds-size-dimension-02: var(--ds-size-dimension-xxs);
  --ds-size-dimension-03: var(--ds-size-dimension-xs);
  --ds-size-dimension-04: var(--ds-size-dimension-s);
  --ds-size-dimension-05: var(--ds-size-dimension-m);
  --ds-size-dimension-06: var(--ds-size-dimension-l);
  --ds-size-dimension-07: var(--ds-size-dimension-xl);
  --ds-size-dimension-08: var(--ds-size-dimension-xxl);
  --ds-size-dimension-09: var(--ds-size-dimension-xxxl);
  --ds-size-dimension-10: var(--ds-size-dimension-xxxxl);
  --ds-layout: var(--ds-spacing-xs);
  --ds-layout-01: var(--ds-spacing-xs);
  --ds-layout-02: var(--ds-spacing-m);
  --ds-layout-03: var(--ds-spacing-l);
  --ds-layout-04: var(--ds-spacing-xl);
  --ds-layout-05: var(--ds-spacing-xxl);
  --ds-layout-06: var(--ds-spacing-xxxl);
  --ds-shadow-300: 0 1px 1px -1px rgba(0,0,0,.15), 0 1px 2px 0 rgba(0,0,0,.1), 0 1px 4px 0 rgba(0,0,0,.1);
  --ds-shadow-500: 0 3px 4px -1px rgba(0,0,0,.15), 0 5px 10px 0 rgba(0,0,0,.1), 0 1px 12px 0 rgba(0,0,0,.1);
  --ds-shadow-700: 0 8px 10px -5px rgba(0,0,0,.2), 0 16px 24px 2px rgba(0,0,0,.14), 0 6px 30px 5px rgba(0,0,0,.1);
  --ds-transition-fast: 150ms;
  --ds-transition-standard: 300ms;
  --ds-color-rainbow-orange: #FF596F;
  --ds-color-rainbow-red: #E41421;
  --ds-color-rainbow-pink: #ED207B;
  --ds-color-rainbow-navy: #6551CC;
  --ds-color-rainbow-turquoise: #47CCCC;
  --ds-color-rainbow-teal: #99CCBB;
  --ds-color-holiday-rainbow-red: #D92D02;
  --ds-color-holiday-rainbow-orange: #FA8F0F;
  --ds-color-holiday-rainbow-turqoise: #51BA88;
  --ds-color-holiday-rainbow-green: #50C581;
  --ds-color-holiday-rainbow-blue: #78EAF5;
}`;

const darkThemeCss = `
:root {
  --ds-color-brand: #E41421;
  --ds-color-primary: #FF596F;
  --ds-color-neutral-50: #241d2b;
  --ds-color-neutral-100: #2e2738;
  --ds-color-neutral-200: #433850;
  --ds-color-neutral-300: #B8BFCA;
  --ds-color-neutral-500: #554863;
  --ds-color-neutral-800: #19124F;
  --ds-color-neutral-900: #07002f;
  --ds-color-success-100: #E7F8EF;
  --ds-color-success-200: #C1F2DA;
  --ds-color-success-500: #47CCCC;
  --ds-color-success-600: #10834C;
  --ds-color-success-700: #0B532F;
  --ds-color-information-100: #ECE9FF;
  --ds-color-information-200: #CDC3FF;
  --ds-color-information-300: #AFA3F2;
  --ds-color-information-500: #6551CC;
  --ds-color-information-600: #5137D7;
  --ds-color-information-700: #36139A;
  --ds-color-warning-100: #FFE9D8;
  --ds-color-warning-200: #FFD6AD;
  --ds-color-warning-500: #FFB266;
  --ds-color-warning-600: #AD5D02;
  --ds-color-warning-700: #723D0A;
  --ds-color-error-100: #FFECEC;
  --ds-color-error-200: #FFCCCB;
  --ds-color-error-500: #dc305b;
  --ds-color-error-600: #CD1C1A;
  --ds-color-error-700: #810D0C;
  --ds-color-destructive-100: #FFECEC;
  --ds-color-destructive-200: #FFCCCB;
  --ds-color-destructive-500: #ED3432;
  --ds-color-destructive-600: #CD1C1A;
  --ds-color-destructive-700: #810D0C;
  --ds-color-white: #f4f4f4;
  --ds-color-black: #000000;
  --ds-color-transparent: transparent;
  --ds-color-expressive-green-100: #E3F8E9;
  --ds-color-expressive-green-300: #51BA88;
  --ds-color-expressive-green-500: #0E844C;
  --ds-color-expressive-green-700: #0B6339;
  --ds-color-expressive-blue-100: #F0F6FF;
  --ds-color-expressive-blue-300: #ADCEFF;
  --ds-color-expressive-blue-500: #0066FF;
  --ds-color-expressive-blue-700: #0652C3;
  --ds-color-expressive-yellow-100: #FAF2D9;
  --ds-color-expressive-yellow-300: #FFE9A1;
  --ds-color-expressive-yellow-500: #FFD54D;
  --ds-color-expressive-yellow-700: #D8A709;
  --ds-color-expressive-red-100: #FFD6DD;
  --ds-color-expressive-red-300: #F77990;
  --ds-color-expressive-red-500: #D92D02;
  --ds-color-expressive-red-700: #9A0D27;
  --ds-color-expressive-pink-100: #FFECF4;
  --ds-color-expressive-pink-300: #FFADCE;
  --ds-color-expressive-pink-500: #D32168;
  --ds-color-expressive-pink-700: #C30652;
  --ds-color-expressive-teal-100: #E3F7F8;
  --ds-color-expressive-teal-300: #5BCBD1;
  --ds-color-expressive-teal-500: #19AEAE;
  --ds-color-expressive-teal-700: #086166;
  --ds-color-expressive-purple-100: #FDDDFF;
  --ds-color-expressive-purple-300: #F1A7F4;
  --ds-color-expressive-purple-500: #C511CE;
  --ds-color-expressive-purple-700: #86338B;
  --ds-color-expressive-orange-100: #FFEBD8;
  --ds-color-expressive-orange-300: #FFAF75;
  --ds-color-expressive-orange-500: #EA872D;
  --ds-color-expressive-orange-700: #B06215;
  --ds-color-border: var(--ds-color-neutral-200);
  --ds-color-border-light: var(--ds-color-neutral-100);
  --ds-color-text: #f4f4f4;
  --ds-color-text-contrast: #1A1239;
  --ds-color-text-muted: #B8BFCA;
  --ds-color-text-muted-contrast: #B8BFCA;
  --ds-color-background-ui: #27212f;
  --ds-color-background-ui-contrast: #f4f4f4;
  --ds-color-background-ui-medium: #393046;
  --ds-color-background-ui-high: #4c415c;
  --ds-color-background-alt-50: rgba(34, 26, 40, 0.60);
  --ds-color-background-alt-100: rgba(51, 43, 62, 0.60);
  --ds-color-background-alt-200: rgba(86, 71, 102, 0.60);
  --ds-color-background-alt-500: rgba(116, 98, 134, 0.60);
  --ds-color-background-app: var(--ds-color-secondary-700);
  --ds-color-background-app-light: var(--ds-color-secondary-700);
  --ds-color-background-app-dark: var(--ds-color-secondary-700);
  --ds-color-background-overlay: rgba(140, 149, 165, 0.6);
  --ds-color-background-overlay-transparent: rgba(140, 149, 165, 0);
  --ds-color-state-disabled-text: var(--ds-color-neutral-300);
  --ds-color-state-disabled-background: var(--ds-color-background-alt-100);
  --ds-color-state-focus-border: var(--ds-color-information-300);
  --ds-color-state-hover-overlay: rgba(255, 255, 255, 0.15);
  --ds-color-state-hover-overlay-contrast: rgba(26, 18, 57, 0.1);
  --ds-color-state-loading-background: var(--ds-color-background-alt-200);
  --ds-color-state-loading-shimmer: var(--ds-color-background-alt-50);
  --ds-font-family: -apple-system, BlinkMacSystemFont, Inter, Arial, sans-serif;
  --ds-font-weight-light: 400;
  --ds-font-weight-normal: 400;
  --ds-font-weight-bold: 600;
  --ds-font-weight-display1: var(--ds-font-weight-bold);
  --ds-font-weight-display2: var(--ds-font-weight-bold);
  --ds-font-weight-display3: var(--ds-font-weight-bold);
  --ds-font-weight-display4: var(--ds-font-weight-normal);
  --ds-font-weight-display5: var(--ds-font-weight-bold);
  --ds-font-weight-display6: var(--ds-font-weight-bold);
  --ds-font-weight-body: var(--ds-font-weight-normal);
  --ds-font-weight-body2: var(--ds-font-weight-normal);
  --ds-font-weight-caption: var(--ds-font-weight-normal);
  --ds-font-size-display1: 36px;
  --ds-font-size-display2: 24px;
  --ds-font-size-display3: 20px;
  --ds-font-size-display4: 20px;
  --ds-font-size-display5: 16px;
  --ds-font-size-display6: 14px;
  --ds-font-size-body: 16px;
  --ds-font-size-body2: 14px;
  --ds-font-size-caption: 12px;
  --ds-line-height-display1: 48px;
  --ds-line-height-display2: 32px;
  --ds-line-height-display3: 30px;
  --ds-line-height-display4: 30px;
  --ds-line-height-display5: 26px;
  --ds-line-height-display6: 24px;
  --ds-line-height-body: 26px;
  --ds-line-height-body2: 24px;
  --ds-line-height-caption: 16px;
  --ds-radius-small: 4px;
  --ds-radius-medium: 8px;
  --ds-radius-large: 12px;
  --ds-radius-pill: 200vh;
  --ds-size-border-width-hairline: 1px;
  --ds-size-border-width-small: 2px;
  --ds-size-border-width-medium: 4px;
  --ds-spacing-xxxs: 2px;
  --ds-spacing-xxs: 4px;
  --ds-spacing-xs: 8px;
  --ds-spacing-s: 12px;
  --ds-spacing-m: 16px;
  --ds-spacing-l: 24px;
  --ds-spacing-xl: 32px;
  --ds-spacing-xxl: 48px;
  --ds-spacing-xxxl: 64px;
  --ds-spacing-legacy-unit: 16px;
  --ds-spacing: var(--ds-spacing-xxxs);
  --ds-spacing-01: var(--ds-spacing-xxxs);
  --ds-spacing-02: var(--ds-spacing-xxs);
  --ds-spacing-03: var(--ds-spacing-xs);
  --ds-spacing-04: var(--ds-spacing-s);
  --ds-spacing-05: var(--ds-spacing-m);
  --ds-spacing-06: var(--ds-spacing-m);
  --ds-spacing-07: var(--ds-spacing-l);
  --ds-spacing-08: var(--ds-spacing-l);
  --ds-spacing-09: var(--ds-spacing-xl);
  --ds-spacing-10: var(--ds-spacing-xxl);
  --ds-size-dimension: var(--ds-spacing-xxs);
  --ds-size-dimension-xxxs: var(--ds-size-dimension);
  --ds-size-dimension-xxs: calc(var(--ds-size-dimension) * 2);
  --ds-size-dimension-xs: calc(var(--ds-size-dimension) * 3);
  --ds-size-dimension-s: calc(var(--ds-size-dimension) * 4);
  --ds-size-dimension-m: calc(var(--ds-size-dimension) * 6);
  --ds-size-dimension-l: calc(var(--ds-size-dimension) * 8);
  --ds-size-dimension-xl: calc(var(--ds-size-dimension) * 10);
  --ds-size-dimension-xxl: calc(var(--ds-size-dimension) * 12);
  --ds-size-dimension-xxxl: calc(var(--ds-size-dimension) * 16);
  --ds-size-dimension-xxxxl: calc(var(--ds-size-dimension) * 24);
  --ds-size-dimension-01: var(--ds-size-dimension-xxxs);
  --ds-size-dimension-02: var(--ds-size-dimension-xxs);
  --ds-size-dimension-03: var(--ds-size-dimension-xs);
  --ds-size-dimension-04: var(--ds-size-dimension-s);
  --ds-size-dimension-05: var(--ds-size-dimension-m);
  --ds-size-dimension-06: var(--ds-size-dimension-l);
  --ds-size-dimension-07: var(--ds-size-dimension-xl);
  --ds-size-dimension-08: var(--ds-size-dimension-xxl);
  --ds-size-dimension-09: var(--ds-size-dimension-xxxl);
  --ds-size-dimension-10: var(--ds-size-dimension-xxxxl);
  --ds-layout: var(--ds-spacing-xs);
  --ds-layout-01: var(--ds-spacing-xs);
  --ds-layout-02: var(--ds-spacing-m);
  --ds-layout-03: var(--ds-spacing-l);
  --ds-layout-04: var(--ds-spacing-xl);
  --ds-layout-05: var(--ds-spacing-xxl);
  --ds-layout-06: var(--ds-spacing-xxxl);
  --ds-shadow-300: 0 4px 4px -4px rgba(0, 0, 0, 0.1);
  --ds-shadow-500: 0 8px 16px -8px rgba(0, 0, 0, 0.1), 0 0px 4px rgba(0, 0, 0, 0.1);
  --ds-shadow-700: 0 16px 64px -16px rgba(0, 0, 0, 0.2), 0 0px 8px rgba(0, 0, 0, 0.05);
  --ds-transition-fast: 150ms;
  --ds-transition-standard: 300ms;
  --ds-color-rainbow-orange: #FF596F;
  --ds-color-rainbow-red: #E41421;
  --ds-color-rainbow-pink: #ED207B;
  --ds-color-rainbow-navy: #6551CC;
  --ds-color-rainbow-turquoise: #47CCCC;
  --ds-color-rainbow-teal: #99CCBB;
  --ds-color-holiday-rainbow-red: #D92D02;
  --ds-color-holiday-rainbow-orange: #FA8F0F;
  --ds-color-holiday-rainbow-turqoise: #51BA88;
  --ds-color-holiday-rainbow-green: #50C581;
  --ds-color-holiday-rainbow-blue: #78EAF5;
  --ds-color-secondary-500: #27212f;
  --ds-color-secondary-700: #1f1a27;
  --ds-interaction-overlay: rgba(249, 249, 251, 0.05);
  --ds-borderColor: #393046;
  --ds-borderColor-light: #393046;
}`;

const DesignSystemProvider = ({ config, children, className, style, ...props }: any) => {
  const toastConfig = get(config, 'useToasts', false);
  const trackingConfig = get(config, 'tracker', {});

  const adjustedConfig = { ...config };
  delete (adjustedConfig as any).tracker;
  if (!(adjustedConfig as any).theme) (adjustedConfig as any).theme = defaultTheme;

  const classes = cnames(`ds-theme-${get(config, 'theme.id')}`,
    {
      [className]: className,
    });

  return (
    <React.Fragment>
      <style>{get(config, 'theme.id') === 'find-your-thing-dark' ? darkThemeCss : defaultThemeCss}</style>
      <DesignSystemContext.Provider value={adjustedConfig} {...props}>
        <TrackingContext.Provider value={trackingConfig}>
          <div className={classes} style={style}>
            { children }
          </div>
          {
            toastConfig && (
              <ToastContainer {...toastConfig} />
            )
          }
        </TrackingContext.Provider>
      </DesignSystemContext.Provider>
    </React.Fragment>
  );
};

export default DesignSystemProvider;
